/**
 * NotificationCenter.jsx — bell button + panel with past notifications.
 *
 * The browser holds the last 200 of every type; the server holds the last
 * 100 errors, so a cleared browser or another device still shows what went
 * wrong. Both lists are merged and de-duplicated by id.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bell, CheckCircle2, XCircle, Info, AlertTriangle, MessageSquare,
  Search, Trash2, X as XIcon,
} from "lucide-react";
import {
  clearLocalNotifications, listNotifications, markAllRead,
  notificationsClient, onNotificationsChanged, unreadCount,
} from "@/utils/notificationCenter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { key: "all",     label: "Tutte" },
  { key: "error",   label: "Errori" },
  { key: "warning", label: "Avvisi" },
  { key: "success", label: "Conferme" },
  { key: "info",    label: "Info" },
];

const ICONS = {
  success: { Icon: CheckCircle2,  className: "text-success" },
  error:   { Icon: XCircle,       className: "text-danger" },
  warning: { Icon: AlertTriangle, className: "text-warning" },
  info:    { Icon: Info,          className: "text-info" },
  message: { Icon: MessageSquare, className: "text-content-secondary" },
};

function timeLabel(iso) {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return "adesso";
  if (mins < 60) return `${mins} min`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)} h`;
  return then.toLocaleDateString("it-IT", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

function NotificationRow({ item }) {
  const { Icon, className } = ICONS[item.type] || ICONS.message;
  return (
    <li
      data-testid={`notification-item-${item.id}`}
      data-type={item.type}
      className="flex gap-2.5 px-3 py-2.5 border-b border-line/60 last:border-0 hover:bg-surface-subtle/30 transition-colors"
    >
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${className}`} />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-content break-words">
          {item.message}
          {item.count > 1 && (
            <span
              data-testid={`notification-count-${item.id}`}
              className="ml-1.5 align-middle text-label font-mono text-content-secondary bg-surface-subtle rounded px-1 py-0.5"
            >
              ×{item.count}
            </span>
          )}
        </p>
        {item.description && (
          <p className="text-xs text-content-muted break-words mt-0.5">{item.description}</p>
        )}
        <p className="text-label text-content-muted mt-1 font-mono truncate">
          {timeLabel(item.at)}
          {item.page ? ` · ${item.page}` : ""}
          {item.source === "server" ? " · dal server" : ""}
        </p>
      </div>
    </li>
  );
}

export function NotificationCenter({ align = "right" }) {
  const [open, setOpen] = useState(false);
  const bellRef = useRef(null);
  const panelRef = useRef(null);
  const [local, setLocal] = useState(() => listNotifications());
  const [remote, setRemote] = useState([]);
  const [unread, setUnread] = useState(() => unreadCount());
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  const refreshLocal = useCallback(() => {
    setLocal(listNotifications());
    setUnread(unreadCount());
  }, []);

  useEffect(() => onNotificationsChanged(refreshLocal), [refreshLocal]);

  // Poll rarely: the event covers this tab, this covers other tabs.
  useEffect(() => {
    const id = setInterval(refreshLocal, 20000);
    return () => clearInterval(id);
  }, [refreshLocal]);

  useEffect(() => {
    if (!open) return;
    notificationsClient.get(`${API}/notifications`)
      .then(({ data }) => setRemote(data.notifications || []))
      .catch(() => setRemote([]));
    markAllRead();
    setUnread(0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector("button, input")?.focus();
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        bellRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const items = useMemo(() => {
    const seen = new Set(local.map((n) => n.id));
    const merged = [...local, ...remote.filter((n) => !seen.has(n.id))];
    merged.sort((a, b) => (a.at < b.at ? 1 : -1));
    const needle = query.trim().toLowerCase();
    return merged.filter((n) => {
      if (filter !== "all" && n.type !== filter) return false;
      if (!needle) return true;
      return `${n.message} ${n.description || ""} ${n.page || ""}`
        .toLowerCase().includes(needle);
    });
  }, [local, remote, filter, query]);

  const clearAll = async () => {
    clearLocalNotifications();
    setRemote([]);
    try {
      await notificationsClient.delete(`${API}/notifications`);
    } catch {
      /* local history is already gone; nothing actionable for the user */
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        ref={bellRef}
        data-testid="notifications-bell-btn"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="Notifiche"
        className="relative flex items-center justify-center w-10 h-10 rounded-full border border-line hover:border-line bg-surface/60 transition-colors"
      >
        <Bell className="w-4 h-4 text-content-secondary" />
        {unread > 0 && (
          <span
            data-testid="notifications-unread-dot"
            className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-danger-solid ring-2 ring-line"
          />
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          aria-label="Notifiche"
          data-testid="notifications-panel"
          className={`absolute ${align === "right" ? "right-0" : "left-0"} mt-2 w-[380px] max-w-[92vw] rounded-card border border-line bg-popover shadow-overlay z-[60] overflow-hidden`}
        >
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-line">
            <h3 className="text-sm font-semibold text-content">Notifiche</h3>
            <div className="flex items-center gap-1">
              <button
                data-testid="notifications-clear-btn"
                onClick={clearAll}
                title="Svuota lo storico"
                aria-label="Svuota lo storico"
                className="flex items-center justify-center w-10 h-10 rounded-md text-content-muted hover:text-danger hover:bg-surface-subtle transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              <button
                data-testid="notifications-close-btn"
                onClick={() => setOpen(false)}
                aria-label="Chiudi le notifiche"
                className="flex items-center justify-center w-10 h-10 rounded-md text-content-muted hover:text-content-secondary hover:bg-surface-subtle transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              >
                <XIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div className="px-3 py-2 space-y-2 border-b border-line">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-content-muted absolute left-2 top-1/2 -translate-y-1/2" />
              <input
                data-testid="notifications-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca nelle notifiche…"
                className="w-full h-10 bg-surface-subtle border border-line rounded-md pl-7 pr-2 text-xs text-content placeholder:text-content-muted focus:outline-none focus-visible:border-focus focus-visible:ring-2 focus-visible:ring-focus/40"
              />
            </div>
            <div className="flex flex-wrap gap-1">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  data-testid={`notifications-filter-${t.key}`}
                  onClick={() => setFilter(t.key)}
                  className={`inline-flex items-center min-h-10 px-3 rounded-full text-label border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus ${
                    filter === t.key
                      ? "bg-surface-subtle text-content-disabled border-line-soft"
                      : "border-line text-content-secondary hover:border-line"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <ul
            data-testid="notifications-list"
            className="max-h-[60vh] overflow-y-auto divide-y-0"
          >
            {items.length === 0 ? (
              <li
                data-testid="notifications-empty"
                className="px-3 py-8 text-center text-xs text-content-muted"
              >
                Nessuna notifica da mostrare.
              </li>
            ) : (
              items.map((item) => <NotificationRow key={item.id} item={item} />)
            )}
          </ul>

          <p className="px-3 py-2 border-t border-line text-label text-content-muted">
            Ultime 200 notifiche in questo browser · gli errori (max 100) restano
            salvati sul tuo account.
          </p>
        </div>
      )}
    </div>
  );
}

export default NotificationCenter;
